import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  ShoppingCart, Check, ArrowRight, ArrowLeft, Star, Shield, Truck, 
  Phone, Mail, MapPin, X, Loader2, CheckCircle2, Package
} from '../components/DeskeyIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BIGIN_GREEN = '#34A853';
const BIGIN_NAVY = '#1A365D';

const StorePage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [razorpayKey, setRazorpayKey] = useState('');
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    quantity: 1
  });

  useEffect(() => {
    fetchProducts();
    fetchRazorpayKey();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/store/products`);
      setProducts(res.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRazorpayKey = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/store/razorpay-key`);
      setRazorpayKey(res.data.key_id);
    } catch (err) {
      console.error('Failed to fetch Razorpay key:', err);
    }
  };

  const handleBuyNow = (product) => {
    setSelectedProduct(product);
    setShowCheckout(true);
  };

  const handlePayment = async () => {
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.shipping_address) {
      alert('Please fill in all fields');
      return;
    }

    setCheckoutLoading(true);

    try {
      // Create order
      const orderRes = await axios.post(`${API_URL}/api/store/create-order`, {
        product_id: selectedProduct.id,
        quantity: formData.quantity,
        ...formData
      });

      const { razorpay_order_id, amount, currency, order_id } = orderRes.data;

      // Initialize Razorpay
      const options = {
        key: razorpayKey,
        amount: amount,
        currency: currency,
        name: 'Deskey',
        description: selectedProduct.name,
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            // Verify payment
            const verifyRes = await axios.post(`${API_URL}/api/store/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            setOrderSuccess({
              orderId: verifyRes.data.order_id,
              productName: selectedProduct.name,
              amount: amount
            });
            setShowCheckout(false);
          } catch (err) {
            alert('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: formData.customer_name,
          email: formData.customer_email,
          contact: formData.customer_phone
        },
        theme: {
          color: BIGIN_GREEN
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error('Payment error:', err);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const formatPrice = (paise) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(paise / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: BIGIN_GREEN }} />
      </div>
    );
  }

  // Order Success Screen
  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: `${BIGIN_GREEN}15` }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: BIGIN_GREEN }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-600 mb-6">Thank you for your purchase</p>
          
          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Order ID:</span>
              <span className="font-medium">{orderSuccess.orderId.slice(0, 12)}...</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Product:</span>
              <span className="font-medium">{orderSuccess.productName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Amount:</span>
              <span className="font-bold" style={{ color: BIGIN_GREEN }}>{formatPrice(orderSuccess.amount)}</span>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 mb-6">
            A confirmation email has been sent to your email address with order tracking details.
          </p>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => { setOrderSuccess(null); }}
            >
              Continue Shopping
            </Button>
            <Button 
              className="flex-1 text-white"
              style={{ background: BIGIN_GREEN }}
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Load Razorpay Script */}
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>
      
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: BIGIN_GREEN }}>
                <span className="text-white font-bold text-sm">D</span>
              </div>
              <span className="text-xl font-bold" style={{ color: BIGIN_NAVY }}>Deskey Store</span>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16" style={{ background: `linear-gradient(135deg, ${BIGIN_GREEN} 0%, #2D9348 100%)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Deskey Hardware Store
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Premium educational devices designed for the modern classroom. Enhance learning with AI-powered hardware.
          </p>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8 lg:gap-16">
            {[
              { icon: Shield, text: '1 Year Warranty' },
              { icon: Truck, text: 'Free Delivery' },
              { icon: Phone, text: '24/7 Support' },
              { icon: Star, text: 'Trusted by 500+ Institutions' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-600">
                <item.icon className="w-5 h-5" style={{ color: BIGIN_GREEN }} />
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <Card key={product.id} className="overflow-hidden group hover:shadow-xl transition-all duration-300">
                {/* Product Image */}
                <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 p-6">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.id === 'deskey_magic_board' && (
                    <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: '#F97316' }}>
                      Best for Classrooms
                    </div>
                  )}
                </div>
                
                {/* Product Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                  
                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {product.features.slice(0, 3).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 flex-shrink-0" style={{ color: BIGIN_GREEN }} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Price & CTA */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-bold" style={{ color: BIGIN_GREEN }}>
                        {formatPrice(product.price)}
                      </span>
                    </div>
                    <Button 
                      onClick={() => handleBuyNow(product)}
                      className="rounded-full text-white font-semibold px-6"
                      style={{ background: BIGIN_GREEN }}
                    >
                      Buy Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Deskey Hardware */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Why Choose <span style={{ color: BIGIN_GREEN }}>Deskey Hardware</span>?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'AI Integration',
                desc: 'All devices come pre-loaded with Deskey AI software for seamless classroom integration.',
                icon: '🤖'
              },
              {
                title: 'Built for Education',
                desc: 'Purpose-built hardware designed specifically for educational environments.',
                icon: '🎓'
              },
              {
                title: 'Instant Sync',
                desc: 'Real-time synchronization between devices, teachers, and students.',
                icon: '⚡'
              }
            ].map((item, i) => (
              <Card key={i} className="p-6 text-center hover:shadow-lg transition-all">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© 2025 Deskey. All rights reserved.</p>
        </div>
      </footer>

      {/* Checkout Modal */}
      {showCheckout && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Complete Your Purchase</h2>
                <button 
                  onClick={() => setShowCheckout(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Product Summary */}
              <div className="flex gap-4 p-4 bg-gray-50 rounded-xl mb-6">
                <img 
                  src={selectedProduct.image} 
                  alt={selectedProduct.name}
                  className="w-20 h-20 object-contain rounded-lg bg-white"
                />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{selectedProduct.name}</h3>
                  <p className="text-2xl font-bold mt-1" style={{ color: BIGIN_GREEN }}>
                    {formatPrice(selectedProduct.price * formData.quantity)}
                  </p>
                </div>
              </div>
              
              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <Input
                    placeholder="Enter your full name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <Input
                    placeholder="Enter your phone number"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Address *</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    rows={3}
                    placeholder="Enter your complete shipping address"
                    value={formData.shipping_address}
                    onChange={(e) => setFormData({...formData, shipping_address: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <div className="flex items-center gap-3">
                    <button 
                      className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                      onClick={() => setFormData({...formData, quantity: Math.max(1, formData.quantity - 1)})}
                    >
                      -
                    </button>
                    <span className="text-lg font-medium w-10 text-center">{formData.quantity}</span>
                    <button 
                      className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                      onClick={() => setFormData({...formData, quantity: formData.quantity + 1})}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Pay Button */}
              <Button 
                onClick={handlePayment}
                disabled={checkoutLoading}
                className="w-full h-12 mt-6 text-white font-semibold text-lg rounded-xl"
                style={{ background: BIGIN_GREEN }}
              >
                {checkoutLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Pay {formatPrice(selectedProduct.price * formData.quantity)}
                  </>
                )}
              </Button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                Secured by Razorpay. Your payment information is encrypted.
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StorePage;
